import axios from 'axios'

const API_PATH = 'admins/finance/bank_transactions'

const BankTransactionsApi = {
  /**
   * Get list of ::Finance::BankTransaction
   * Invoke `GET admins/finance/bank_transactions`
   * @param {Integer} page - current page
   * @param {Integer} per_page - how many data on each page
   * @param {String} sort_by - sort by, created_at/updated_at/transfer_date
   * @param {String} sort_order - sort order, asc/desc
   * @param {tl_bank_id} amount - transaction record bank id, TlBank.id
   * @param {String} transfer_date - transaction bank name, TlBank.bank
   * @param {String} bank_account_number - transaction bank account number, TlBank.account_number
   * @param {String} category - transaction category, Finance::BankTransaction.category
   * @param {String} description - description of a transaction, Finance::BankTransaction.description
   * @param {String} order_number - Finance can filter by order number that linked to a bank transaction, Customer::Order.number
   * @param {String} cash_flow - transaction cashflow, credit/debit, Finance::BankTransaction.cash_flow
   * @param {Number} amount - transaction amount total, Finance::BankTransaction.amount
   * @param {Date} transfer_date - transaction transfer date, Finance::BankTransaction.transfer_date
   * @param {Date} created_at - transaction create date, Finance::BankTransaction.created_at
   * @param {Date} updated_at - transaction latest update_at date, Finance::BankTransaction.updated_at
   */
  get: async ({
    page = 1,
    per_page = 10,
    sort_by = 'transfer_date',
    sort_order = 'desc',
    tl_bank_id = '',
    bank = '',
    bank_account_number = '',
    category = '',
    description = '',
    order_number = '',
    cash_flow = '',
    amount = '',
    transfer_date = '',
    created_at = '',
    updated_at = ''
  }) => {
    const params   = {
      page                : page,
      per_page            : per_page,
      sort_by             : sort_by,
      sort_order          : sort_order,
      tl_bank_id          : tl_bank_id,
      bank                : bank,
      bank_account_number : bank_account_number,
      category            : category,
      description         : description,
      order_number        : order_number,
      cash_flow           : cash_flow,
      amount              : amount,
      transfer_date       : transfer_date,
      created_at          : created_at,
      updated_at          : updated_at
    }
    const response = await axios.get(API_PATH, { params })
    return response
  },
  /**
   * Create ::Finance::BankTransaction record
   * Invoke `POST admins/finance/bank_transactions`
   * @param {Object} tl_bank - ::TlBank
   * @param {String} category - Finance::BankTransaction.category
   * @param {String} description - Finance::BankTransaction.description
   * @param {String} cash_flow - Finance::BankTransaction.cash_flow
   * @param {Integer} amount - Finance::BankTransaction.amount
   * @param {String} transfer_date - Finance::BankTransaction.transfer_date
   */
  create: async ({
    tl_bank,
    category,
    description,
    cash_flow,
    amount,
    transfer_date
  }) => {
    const body     = {
      tl_bank_id    : tl_bank.id,
      category      : category,
      description   : description,
      cash_flow     : cash_flow,
      amount        : amount,
      transfer_date : transfer_date
    }
    const response = await axios.post(API_PATH, body)
    return response
  },

  /**
   * Get a ::Finance::BankTransaction record
   * Invoke `GET admins/finance/bank_transactions/:id`
   * @param {Integer} id - ::Finance::BankTransaction.id
   */
  show: async (id) => {
    const path = [
      API_PATH,
      id
    ].join('/')

    const response = await axios.get(path)
    return response
  },

  /**
   * Update a ::Finance::BankTransaction record
   * Invoke `PATCH admins/finance/bank_transactions/:id`
   * @param {Integer} id - ::Finance::BankTransaction.id
   * @param {Object} tl_bank - ::TlBank
   * @param {String} category - Finance::BankTransaction.category
   * @param {String} description - Finance::BankTransaction.description
   * @param {String} cash_flow - Finance::BankTransaction.cash_flow
   * @param {Integer} amount - Finance::BankTransaction.amount
   * @param {String} transfer_date - Finance::BankTransaction.transfer_date
   */
  update: async ({
    id,
    tl_bank,
    category,
    description,
    cash_flow,
    amount,
    transfer_date
  }) => {
    const body = {
      tl_bank_id    : tl_bank.id,
      category      : category,
      description   : description,
      cash_flow     : cash_flow,
      amount        : amount,
      transfer_date : transfer_date
    }

    const path = [
      API_PATH,
      id
    ].join('/')

    const response = await axios.patch(path, body)
    return response
  },
  /**
   * Link Customer::Order to ::Finance::BankTransaction
   * Invoke `PATCH admins/finance/bank_transactions/:id/order_link`
   * @param {Number} id - ::Finance::BankTransaction.id
   * @param {Array} customerOrderNumbers - array of Customer::Order number
   */
  orderLink: async ({
    id,
    customerOrderNumbers
  }) => {
    const body = {
      customer_order_numbers: customerOrderNumbers
    }

    const path = [
      API_PATH,
      id,
      'order_link'
    ].join('/')

    const response = await axios.patch(path, body)
    return response
  },
  /**
   * Automatically Link Customer::Order to ::Finance::BankTransaction
   * Invoke `PATCH admins/finance/bank_transactions/:id/order_auto_link`
   * @param {Number} id - ::Finance::BankTransaction.id
   */
  orderAutoLink: async ({
    id
  }) => {
    const path = [
      API_PATH,
      id,
      'order_auto_link'
    ].join('/')

    const response = await axios.patch(path)
    return response
  },
  /**
   * Remove relation between Customer::Order and ::Finance::BankTransaction
   * Invoke `PATCH admins/finance/bank_transactions/:id/remove_order`
   * @param {Number} id - ::Finance::BankTransaction.id
   * @param {Array} customerOrderNumbers - array of Customer::Order number
   */
  orderRemove: async ({
    id,
    customerOrderNumbers
  }) => {
    const body = {
      customer_order_numbers: customerOrderNumbers
    }

    const path = [
      API_PATH,
      id,
      'order_remove'
    ].join('/')

    const response = await axios.patch(path, body)
    return response
  },

  /**
   * Post bulk  CSV file to bulk insert the bank transaction data
   * Invoke `POST /admins/finance/bank_transactions/bulk_create_from_csv`
   * @param {object} file - CSV file
   * @param {integer} tl_bank_id - ::Finance::BankTransaction.id
   */
  bulkCreateCsv: async ( { file, tl_bank_id } ) => {
    const path  = `${API_PATH}/bulk_create_from_csv`
    let payload = new FormData()
    payload.append('file', file)
    payload.append('tl_bank_id', tl_bank_id)
    const response = await axios.post(path, payload)
    return response
  }
}

export default BankTransactionsApi
